"use client"

/**
 * The shared vocabulary of the question builder.
 *
 * Two surfaces let an author work on the body of a question: the composer
 * (`new-library-item-form.tsx`) and the import review editor
 * (`question-import/import-item-editor.tsx`). They are the same job seen from
 * two directions, so a choice row, a section heading, and the letter chip that
 * marks the answer key have to look and behave identically in both. Left as
 * private helpers in the composer, the import editor grew its own radios and
 * its own headings, and the second screen taught authors a different set of
 * habits for the same task.
 *
 * These are presentational only: no form library, no question model. Callers
 * bring their own state, which is why the composer can drive them from
 * react-hook-form while the import editor drives them from a store record.
 */

import * as React from "react"

import { cn } from "@/lib/utils"
import { builderListIndexClass } from "@/components/question-builder-primitives-shared"

/** Shared list-row chrome — ordering, choices, blanks, pairs use the same card row. */
/** A solid surface, like the composer's text fields, so the builder's dot canvas does not show through. */
export const BUILDER_LIST_ROW =
  "flex items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 dark:bg-muted/35"

export const BUILDER_LIST_FIELD =
  "h-11 min-w-0 flex-1 border-0 bg-transparent px-0 text-base font-medium shadow-none focus-visible:ring-0 md:text-lg"

export const BUILDER_LIST_LOCKED =
  "flex h-11 min-w-0 flex-1 items-center text-base font-medium text-muted-foreground md:text-lg"

/**
 * The letter chip that doubles as the answer key.
 *
 * Marking the key by clicking the letter, rather than by a radio beside it,
 * is the composer's pattern: the chip is already the thing the author points
 * at when they say "B is correct", and a correct chip carries a check instead
 * of its letter so the key survives a black-and-white print or a colour-blind
 * reader (SC 1.4.1).
 */
export function BuilderAnswerChip({
  letter,
  correct,
  disabled,
  onClick,
  ariaLabel,
}: {
  letter: string
  correct: boolean
  disabled?: boolean
  onClick: () => void
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={correct}
      aria-label={ariaLabel}
      className={cn(
        builderListIndexClass(7),
        "text-sm transition-colors",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        correct &&
          "border-chart-2 bg-[var(--icon-disc-chart-2-bg)] text-[var(--icon-disc-chart-2-fg)] ring-1 ring-chart-2/40"
      )}
    >
      {correct ? (
        <i className="fa-solid fa-check text-xs leading-none" aria-hidden="true" />
      ) : (
        letter
      )}
    </button>
  )
}

export function BuilderSection({
  title,
  required,
  /** Muted meta line directly under the title row (tight spacing). */
  description,
  /** Right-aligned controls (e.g. Suggest choices, Add references). */
  actions,
  hint,
  headerActions,
  children,
}: {
  title: string
  required?: boolean
  description?: React.ReactNode
  actions?: React.ReactNode
  hint?: React.ReactNode
  headerActions?: React.ReactNode
  children: React.ReactNode
}) {
  const trailing = actions ?? hint

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <h2 className="text-sm font-semibold text-foreground">
              {title}
              {required ? (
                <span className="ms-1 text-destructive" aria-hidden="true">
                  *
                </span>
              ) : null}
            </h2>
            {headerActions}
          </div>
          {description ? (
            typeof description === "string" ? (
              <p className="text-xs leading-snug text-muted-foreground">
                {description}
              </p>
            ) : (
              description
            )
          ) : null}
        </div>
        {trailing ? (
          typeof trailing === "string" ? (
            <span className="shrink-0 self-center text-xs text-muted-foreground">
              {trailing}
            </span>
          ) : (
            <div className="flex shrink-0 items-center gap-2 self-center">
              {trailing}
            </div>
          )
        ) : null}
      </div>
      {children}
    </section>
  )
}

export function BuilderOptionList({
  "aria-label": ariaLabel,
  children,
  className,
}: {
  "aria-label": string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("space-y-2", className)} role="group" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

export function BuilderListRow({
  children,
  className,
  highlighted,
  ...props
}: React.ComponentProps<"div"> & {
  highlighted?: boolean
}) {
  return (
    <div
      {...props}
      className={cn(
        BUILDER_LIST_ROW,
        highlighted &&
          "border-chart-2/50 ring-1 ring-chart-2/35 ring-inset dark:border-chart-2/60",
        className
      )}
    >
      {children}
    </div>
  )
}

export function BuilderListRowActions({
  children,
}: {
  children: React.ReactNode
}) {
  return <div className="flex shrink-0 items-center gap-0.5">{children}</div>
}
